import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { useSwitchStaffOrg, useSwitchTalentOrg } from "../../app/session";
import { WEBSITE_URL } from "../../app/nav";
import { Loading } from "../../components/States";
import { currentLang, dateOf, t, tr, type Lang } from "../../lib/sprache";
import { toast } from "../../components/Toast";
import { recordConsent } from "./actions";
import { describeCreatorError } from "./fehler";

/**
 * Prüf-Chat CP-001 (02.11.2026): Eine Creatorin mit offener Einladung meldete sich ohne den Einladungslink an und landete in
 * „Wie heißt deine Agentur?“. Vor dem Anlegen einer Agentur fragen wir deshalb offene Einladungen an die eigene
 * Anmeldeadresse ab (my_open_invitations, 0458) und bieten das Annehmen an (accept_my_invitation). Ohne Einladung, bei einem
 * Fehler der Abfrage oder auf ausdrücklichen Wunsch („trotzdem eigene Agentur anlegen“) erscheint der bisherige Inhalt.
 */

export interface OpenInvitation {
  id: string;
  agency: string;
  kind: "talent" | "member";
  expires_at: string;
  guardian_required: boolean;
}

export async function loadOpenInvitations(): Promise<OpenInvitation[]> {
  const r = await api.read<OpenInvitation[] | null>("my_open_invitations", {});
  return Array.isArray(r) ? r : [];
}

/**
 * Sicherheitsprüfung (Inhaber 02.11.2026, 0470): Offene Einladungen zeigt die Datenbank nur an eine bestätigte Adresse. Ist sie
 * noch unbestätigt, nennt sie nur die Zahl (ohne Agentur); dann zuerst bestätigen oder den Link aus der Einladungs-Mail öffnen.
 */
export async function loadUnverifiedInvitationCount(): Promise<number> {
  const r = await api.read<number | null>("my_open_invitations_unverified", {});
  return typeof r === "number" && r > 0 ? r : 0;
}

export function OffeneEinladungen({ children }: { children: ReactNode }) {
  const userId = api.session()?.userId ?? null;
  const q = useQuery({
    queryKey: ["my_open_invitations", userId], enabled: !!userId, staleTime: 30_000,
    // Ältere Datenbank ohne die Funktion: einfach weiter wie bisher
    queryFn: () => loadOpenInvitations().catch(() => [] as OpenInvitation[]),
  });
  const offen = useQuery({
    queryKey: ["my_open_invitations_unverified", userId], enabled: !!userId && q.isSuccess && !q.data?.length, staleTime: 30_000,
    queryFn: () => loadUnverifiedInvitationCount().catch(() => 0),
  });
  const [trotzdem, setTrotzdem] = useState(false);
  const [zugestimmt, setZugestimmt] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gesendet, setGesendet] = useState<string | null>(null);
  const switchTalent = useSwitchTalentOrg();
  const switchStaff = useSwitchStaffOrg();
  const navigate = useNavigate();

  if (!userId || trotzdem) return <>{children}</>;
  if (q.isPending) return <Loading />;
  const list = q.data ?? [];
  if (!list.length && offen.isPending && offen.fetchStatus !== "idle") return <Loading />;
  if (!list.length && (offen.data ?? 0) > 0) {
    const nochmal = async () => {
      setBusy("mail");
      setError(null);
      try {
        const r = await api.rpc<{ status: string }>("email_verification_resend");
        setGesendet(r.status === "warten"
          ? t("Du hast gerade erst eine Mail bekommen. Bitte schau in dein Postfach oder versuch es in 10 Minuten noch einmal.", "You only just received an email. Please check your inbox or try again in 10 minutes.")
          : t("Wir haben dir den Link noch einmal geschickt. Schau auch im Spam-Ordner nach.", "We have sent you the link again. Also check your spam folder."));
      } catch (e) {
        const d = describeCreatorError(e, currentLang());
        setError(`${d.title} ${d.text}`.trim());
      } finally {
        setBusy(null);
      }
    };
    return (
      <div className="panel stack tk-offene-einladung" role="region" aria-labelledby="offene-einladung-titel">
        <h2 id="offene-einladung-titel">{t("Für deine E-Mail-Adresse liegt eine Einladung vor.", "There is an invitation for your email address.")}</h2>
        <p>{t("Bestätige zuerst deine Adresse über den Link in unserer Mail; danach kannst du die Einladung hier annehmen. Oder öffne direkt den Link aus deiner Einladungs-Mail.",
          "First confirm your address using the link in our email; you can then accept the invitation here. Or open the link from your invitation email directly.")}</p>
        {gesendet && <p role="status">{gesendet}</p>}
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row">
          <button type="button" className="primary" disabled={busy !== null} aria-busy={busy === "mail"} onClick={() => void nochmal()}>
            {busy === "mail" ? t("Wird gesendet …", "Sending …") : t("Bestätigungsmail erneut senden", "Send confirmation email again")}
          </button>
          <button type="button" className="quiet" disabled={busy !== null} onClick={() => setTrotzdem(true)}>{t("Ich möchte trotzdem eine eigene Agentur anlegen", "I still want to create my own agency")}</button>
        </div>
      </div>
    );
  }
  if (!list.length) return <>{children}</>;

  const annehmen = async (inv: OpenInvitation) => {
    if (!zugestimmt) {
      setError(t("Bitte zuerst die Nutzungsbedingungen akzeptieren.", "Please accept the terms of use first."));
      return;
    }
    setBusy(inv.id);
    setError(null);
    try {
      const r = await api.rpc<{ org_id: string; kind: "member" | "talent" }>("accept_my_invitation", { p_invitation_id: inv.id });
      await recordConsent(api).catch(() => undefined);
      if (r.kind === "member") switchStaff(r.org_id);
      else switchTalent(r.org_id);
      navigate(r.kind === "member" ? "/agentur" : "/creator", { replace: true });
    } catch (e) {
      const d = describeCreatorError(e, currentLang());
      setError(`${d.title} ${d.text}`.trim());
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="panel stack tk-offene-einladung" role="region" aria-labelledby="offene-einladung-titel">
      <h2 id="offene-einladung-titel">{list.length === 1
        ? t(`Du hast eine offene Einladung von ${list[0]!.agency}.`, `You have an open invitation from ${list[0]!.agency}.`)
        : t(`Du hast ${list.length} offene Einladungen.`, `You have ${list.length} open invitations.`)}</h2>
      <p>{t("Nimm die Einladung an, um mit deiner Agentur zu arbeiten. Eine eigene Agentur brauchst du dafür nicht.",
        "Accept the invitation to work with your agency. You do not need an agency of your own for that.")}</p>
      <label className="row tk-check">
        <input type="checkbox" checked={zugestimmt} onChange={(e) => setZugestimmt(e.target.checked)} />
        <span>
          {t("Ich akzeptiere die ", "I accept the ")}<a href={`${WEBSITE_URL}/nutzung.html`} target="_blank" rel="noopener noreferrer">{t("Nutzungsbedingungen", "terms of use")}</a>{t(" und habe die ", " and have read the ")}<a href={`${WEBSITE_URL}/datenschutz.html`} target="_blank" rel="noopener noreferrer">{t("Datenschutzhinweise", "privacy notice")}</a>{t(" gelesen.", ".")}
        </span>
      </label>
      <ul className="tk-tasks">
        {list.map((inv) => (
          <li key={inv.id} className="row">
            <span>
              <strong>{inv.agency}</strong>
              <small>{` · ${inv.kind === "member" ? t("Zugang fürs Team", "Team access") : t("Creator-Portal", "Creator portal")} · ${t("gültig bis", "valid until")} ${dateOf(inv.expires_at, currentLang())}`}</small>
            </span>
            {inv.guardian_required
              // Minderjährige: die Einwilligung der Eltern gehört zur Annahme und steht auf der Einladungsseite
              ? <small>{t("Du bist unter 18: Bitte öffne den Link aus deiner Einladungs-Mail und gib dort die Adresse deiner erziehungsberechtigten Person an. Nach ihrer Bestätigung nimmst du hier an.",
                "You are under 18: please open the link from your invitation email and enter the address of your parent or guardian there. After their confirmation you accept here.")}</small>
              : <button type="button" className="primary" disabled={busy !== null} aria-busy={busy === inv.id} onClick={() => void annehmen(inv)}>
                {busy === inv.id ? t("Wird angenommen …", "Accepting …") : t("Einladung annehmen", "Accept invitation")}
              </button>}
          </li>
        ))}
      </ul>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button type="button" className="quiet" disabled={busy !== null} onClick={() => setTrotzdem(true)}>{t("Ich möchte trotzdem eine eigene Agentur anlegen", "I still want to create my own agency")}</button>
      </div>
    </div>
  );
}

/** „Später“ im Hinweis gilt für diese Sitzung (Speicher gesperrt: der Hinweis kommt beim nächsten Laden wieder). */
const SPAETER = "tk-einladung-spaeter";
function spaeterGemerkt(): string[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(SPAETER) ?? "[]") as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function spaeterMerken(ids: string[]): void {
  try {
    sessionStorage.setItem(SPAETER, JSON.stringify(ids));
  } catch {
    /* ohne Speicher nur bis zum Neuladen */
  }
}

/**
 * Prüf-Chat CP-001b (Nachtest 02.11.2026): Wer schon Creator einer Agentur ist, sah eine offene Einladung einer weiteren
 * Agentur im Portal nirgends (nur auf der Registrierungsseite). Der Hinweis im Rahmen des Creator-Portals bietet das Annehmen
 * mit dem bestehenden Konto an (my_open_invitations, accept_my_invitation aus 0458); „Später“ blendet ihn für die Sitzung aus.
 * Die Nutzungsbedingungen hat das Konto schon akzeptiert; Minderjährige gehen über den Link aus der Mail (Einwilligung der Eltern).
 */
export function EinladungsHinweis({ lang }: { lang: Lang }) {
  const tl = (de: string, en: string) => tr(lang, de, en);
  const userId = api.session()?.userId ?? null;
  const q = useQuery({
    queryKey: ["my_open_invitations", userId], enabled: !!userId, staleTime: 60_000,
    queryFn: () => loadOpenInvitations().catch(() => [] as OpenInvitation[]),
  });
  const [spaeter, setSpaeter] = useState<string[]>(spaeterGemerkt);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const switchTalent = useSwitchTalentOrg();
  const switchStaff = useSwitchStaffOrg();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const list = (q.data ?? []).filter((i) => !spaeter.includes(i.id));
  if (!list.length) return null;

  const annehmen = async (inv: OpenInvitation) => {
    setBusy(inv.id);
    setError(null);
    try {
      const r = await api.rpc<{ org_id: string; kind: "member" | "talent" }>("accept_my_invitation", { p_invitation_id: inv.id });
      toast(tl(`Einladung angenommen. Du bist jetzt auch bei ${inv.agency}.`, `Invitation accepted. You are now also with ${inv.agency}.`));
      // Erst das Konto mit der neuen Verknüpfung laden, dann wechseln: sonst fällt der Rahmen mangels Verknüpfung auf die
      // bisherige Agentur zurück und merkt sich diese
      await qc.invalidateQueries({ queryKey: ["account"] });
      if (r.kind === "member") switchStaff(r.org_id);
      else switchTalent(r.org_id);
      navigate(r.kind === "member" ? "/agentur" : "/creator", { replace: true });
    } catch (e) {
      const d = describeCreatorError(e, lang);
      setError(`${d.title} ${d.text}`.trim());
    } finally {
      setBusy(null);
    }
  };
  const nichtJetzt = (id: string) => {
    const neu = [...spaeter, id];
    setSpaeter(neu);
    spaeterMerken(neu);
  };

  return (
    <div className="content tk-rahmen-hinweise">
      {list.map((inv) => (
        <div key={inv.id} className="tk-start-banner tk-einladung-hinweis" role="region" aria-label={tl(`Einladung von ${inv.agency}`, `Invitation from ${inv.agency}`)}>
          <span className="tk-start-banner-text">
            <strong>{tl(`Einladung von ${inv.agency}`, `Invitation from ${inv.agency}`)}</strong>
            <small>{inv.guardian_required
              ? tl("Du bist unter 18: Bitte öffne den Link aus deiner Einladungs-Mail; dort gibt deine erziehungsberechtigte Person ihre Einwilligung.",
                "You are under 18: please open the link from your invitation email; your parent or guardian gives their consent there.")
              : inv.kind === "member"
                ? tl(`${inv.agency} lädt dich in ihr Team ein. Du nimmst die Einladung mit diesem Konto an; gültig bis ${dateOf(inv.expires_at, lang)}.`,
                  `${inv.agency} invites you to join their team. You accept the invitation with this account; valid until ${dateOf(inv.expires_at, lang)}.`)
                : tl(`${inv.agency} lädt dich in ihr Creator-Portal ein. Du nimmst die Einladung mit diesem Konto an; deine anderen Agenturen bleiben, wie sie sind. Gültig bis ${dateOf(inv.expires_at, lang)}.`,
                  `${inv.agency} invites you to their creator portal. You accept the invitation with this account; your other agencies stay as they are. Valid until ${dateOf(inv.expires_at, lang)}.`)}</small>
            {error && busy === null && <small className="tk-error" role="alert">{error}</small>}
          </span>
          {!inv.guardian_required && (
            <button type="button" className="primary" disabled={busy !== null} aria-busy={busy === inv.id} onClick={() => void annehmen(inv)}>
              {busy === inv.id ? tl("Wird angenommen …", "Accepting …") : tl("Einladung annehmen", "Accept invitation")}
            </button>
          )}
          <button type="button" disabled={busy !== null} onClick={() => nichtJetzt(inv.id)}>{tl("Später", "Later")}</button>
        </div>
      ))}
    </div>
  );
}
